import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useDemo } from "../context/DemoContext";
import { Button } from "./ui/button";
import { X, ChevronLeft, ChevronRight, Play, Pause } from "lucide-react";

const STEPS = [
  {
    route: "/",
    title: "Panel del dueño",
    text: "Arrancamos acá: todo lo que pasó mientras la oficina estaba cerrada. Consultas fuera de horario, visitas de la semana, cobros por cobrar, reclamos y el ranking de tus vendedores.",
  },
  {
    route: "/asistente",
    title: "Asistente 24/7",
    text: "La oficina cierra a las 18 y el asistente sigue respondiendo por WhatsApp y web con la ficha de cada propiedad. Mirá el simulador: escribí como un interesado a las 21:30 y te responde al toque.",
  },
  {
    route: "/visitas",
    title: "Coordinación de visitas",
    text: "Para deptos ocupados, el inquilino marca franjas, el interesado elige una y se confirma a los tres. Salen recordatorios solos. Para los libres, se agenda directo con el agente.",
  },
  {
    route: "/agenda",
    title: "Agenda semanal",
    text: "La semana de cada agente en una sola vista: visitas, firmas, vencimientos y tareas. Arrastrás un evento y lo movés de día u horario.",
  },
  {
    route: "/alquileres",
    title: "Administración de alquileres",
    text: "Contratos con buscador y filtros que funcionan igual con 5 o 500. Hacé click en un contrato y vas a ver la ficha completa: cuenta corriente, ajustes por ICL, reclamos, check-in/out y liquidación en PDF.",
  },
  {
    route: "/tareas",
    title: "Tareas por vendedor",
    text: "Repartí el trabajo del equipo: asignás tareas a cada vendedor con vencimiento y prioridad, y seguís el avance.",
  },
  {
    route: "/propiedades",
    title: "Propiedades e interesados",
    text: "Fichas claras y comparador lado a lado (precio por m², expensas, amenities). Y el seguimiento de cada interesado con su vendedor asignado.",
  },
  {
    route: "/configuracion",
    title: "Se adapta a tu inmobiliaria",
    text: "El cierre que vende: cambiás nombre, monograma y color, y toda la app se vuelve la tuya al instante. Lo que viste ya es tu inmobiliaria.",
  },
];

export function DemoTour() {
  const { active, stop } = useDemo();
  const navigate = useNavigate();
  const [i, setI] = useState(0);
  const [playing, setPlaying] = useState(true);
  const timer = useRef(null);

  useEffect(() => {
    if (active) {
      setI(0);
      setPlaying(true);
    }
  }, [active]);

  useEffect(() => {
    if (active) navigate(STEPS[i].route);
  }, [i, active]); // eslint-disable-line

  useEffect(() => {
    if (!active || !playing) return;
    timer.current = setTimeout(() => setI((prev) => (prev < STEPS.length - 1 ? prev + 1 : prev)), 7000);
    return () => clearTimeout(timer.current);
  }, [i, active, playing]);

  if (!active) return null;
  const step = STEPS[i];
  const last = i === STEPS.length - 1;

  return (
    <div className="fixed inset-x-0 bottom-0 z-[60] p-4 flex justify-center pointer-events-none">
      <div className="pointer-events-auto w-full max-w-2xl rounded-lg border border-slate-200 bg-white shadow-xl" data-testid="demo-tour">
        <div className="flex items-start gap-3 p-4">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-brand text-white text-sm font-bold">{i + 1}</div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm font-semibold text-slate-900">{step.title}</p>
              <span className="text-xs text-slate-400 shrink-0">Paso {i + 1} de {STEPS.length}</span>
            </div>
            <p className="mt-1 text-sm text-slate-600 leading-relaxed">{step.text}</p>
            <div className="mt-3 h-1 w-full rounded-full bg-slate-100 overflow-hidden">
              <div className="h-full bg-brand transition-all duration-500" style={{ width: `${((i + 1) / STEPS.length) * 100}%` }} />
            </div>
          </div>
        </div>
        <div className="flex items-center justify-between border-t border-slate-100 px-4 py-2">
          <Button variant="ghost" size="sm" className="rounded-md" data-testid="demo-exit" onClick={stop}>
            <X className="mr-1 h-4 w-4" /> Salir
          </Button>
          <div className="flex items-center gap-1.5">
            <Button variant="outline" size="sm" className="rounded-md" data-testid="demo-prev" disabled={i === 0} onClick={() => setI((p) => Math.max(0, p - 1))}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" className="rounded-md" data-testid="demo-playpause" onClick={() => setPlaying((p) => !p)}>
              {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
            </Button>
            {last ? (
              <Button size="sm" className="btn-brand rounded-md" data-testid="demo-finish" onClick={stop}>Terminar</Button>
            ) : (
              <Button size="sm" className="btn-brand rounded-md" data-testid="demo-next" onClick={() => setI((p) => Math.min(STEPS.length - 1, p + 1))}>
                Siguiente <ChevronRight className="ml-1 h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
